import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/SsoCallbackPage.css';

type Status = 'processing' | 'error';

const mapSsoErrorMessage = (code?: string | null): string => {
  if (!code) {
    return 'Unable to complete Microsoft sign-in. Please try again.';
  }

  const normalized = code.toLowerCase();

  switch (normalized) {
    case 'invalid_state':
      return 'Your sign-in session expired. Please start the Microsoft login again.';
    case 'missing_code':
      return 'Microsoft did not return a valid authorization code. Please try again.';
    case 'sso_not_configured':
      return 'Microsoft single sign-on is not available at the moment.';
    case 'email_not_found':
      return 'We could not read your Microsoft email address. Please make sure the account has an email.';
    case 'domain_not_allowed':
      return 'This Microsoft email domain is not allowed for sign-in.';
    case 'user_not_found':
      return 'We could not find an account in our system for this Microsoft email.';
    case 'user_not_verified':
      return 'Your account is not verified yet. Please verify your email before using Microsoft sign-in.';
    case 'user_inactive':
      return 'Your account is inactive. Please contact your administrator.';
    default:
      return code;
  }
};

const SsoCallbackPage: React.FC = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<Status>('processing');
  const [message, setMessage] = useState('Completing Microsoft sign-in...');
  const hasProcessedRef = useRef(false);
  const isActiveRef = useRef(true);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessedRef.current) {
      return;
    }

    isActiveRef.current = true;

    const params = new URLSearchParams(location.search);
    const provider = params.get('provider');
    const success = params.get('success');
    const token = params.get('token');
    const errorParam = params.get('error');

    // Debug logging
    console.log('🔍 SSO Callback Debug:', {
      provider,
      success,
      hasToken: !!token,
      tokenLength: token?.length,
      errorParam,
      fullUrl: location.search
    });

    if (provider !== 'microsoft') {
      setStatus('error');
      setMessage('Unsupported sign-in provider.');
      hasProcessedRef.current = true;
      return () => {
        isActiveRef.current = false;
      };
    }

    if (success === 'true' && token) {
      hasProcessedRef.current = true;
      console.log('🚀 Starting loginWithToken...');
      loginWithToken(token)
        .then(result => {
          console.log('🔍 loginWithToken promise resolved:', result);

          if (result.success && result.user) {
            console.log('✅ Login successful, redirecting based on user role...');
            
            // Determine redirect path based on user role (same logic as App.tsx)
            const isAdmin = result.user.role === 'admin' || result.user.is_admin === true;
            const isUserAdmin = result.user.role === 'useradmin';
            
            let redirectPath = '/dashboard/user/inbox/open'; // Default for regular users
            if (isAdmin || isUserAdmin) {
              redirectPath = '/dashboard/customers';
            }
            
            console.log(`🔍 Redirecting to: ${redirectPath} (role: ${result.user.role}, is_admin: ${result.user.is_admin})`);
            
            // Use window.location.href to ensure redirect happens even if component unmounts
            // This is more reliable than navigate() when state updates might cause unmount
            window.location.href = redirectPath;
          } else if (result.success && !result.user) {
            console.error('❌ Login succeeded but user data missing');
            if (isActiveRef.current) {
              setStatus('error');
              setMessage('Login succeeded but user data is missing. Please try again.');
            }
          } else {
            console.error('❌ Login failed:', result.error);
            if (isActiveRef.current) {
              setStatus('error');
              setMessage(result.error || 'Unable to complete Microsoft sign-in. Please try again.');
            }
          }
        })
        .catch((error) => {
          console.error('❌ SSO login exception:', error);
          if (isActiveRef.current) {
            setStatus('error');
            setMessage('Unable to complete Microsoft sign-in. Please try again.');
          }
        });
    } else {
      setStatus('error');
      setMessage(mapSsoErrorMessage(errorParam));
      hasProcessedRef.current = true;
    }

    return () => {
      isActiveRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Only depend on location.search to prevent infinite loops

  return (
    <div className="sso-callback-page">
      <div className="sso-card">
        <div className="sso-icon">
          <svg viewBox="0 0 23 23" focusable="false">
            <rect width="10" height="10" x="1" y="1" fill="#f25022" />
            <rect width="10" height="10" x="12" y="1" fill="#7fba00" />
            <rect width="10" height="10" x="1" y="12" fill="#00a4ef" />
            <rect width="10" height="10" x="12" y="12" fill="#ffb900" />
          </svg>
        </div>
        <h1>Microsoft Sign-In</h1>
        <p>{message}</p>
        {status === 'processing' ? (
          <div className="sso-spinner" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="sso-back-button"
            onClick={() => navigate('/signin', { replace: true })}
          >
            Back to login
          </button>
        )}
      </div>
    </div>
  );
};

export default SsoCallbackPage;

